import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { doc, onSnapshot } from 'firebase/firestore'
import { auth, db, googleProvider, appleProvider } from '@/lib/firebase'
import { identifyUser } from '@/lib/analytics'
import { fetchWebAccess, type WebAccessResult } from '@/billing/access'
import { clearInsightsCache } from '@/services/insights'
import { clearGlycemicInsightsCache } from '@/services/patterns'
import { clearAllPhotoStash } from '@/lib/photoStash'
import { normalizeProfile } from '@/services/firebase/firestore'
import type { UserProfile } from '@/types'

/**
 * Web access state:
 *  - 'checking'    → still resolving (auth / profile / server verify in flight)
 *  - 'granted'     → the server confirmed an active Pro entitlement for THIS uid
 *  - 'denied'      → the server confirmed there is no active subscription
 *  - 'unverified'  → the server could not verify (offline, RevenueCat outage,
 *                    callable error). Not access — the gate offers a retry.
 *
 * Access is granted ONLY from a successful server verdict tied to the current
 * uid. The client-writable `fsnap_users.isPremium` mirror never grants access.
 */
export type AccessState = 'checking' | 'granted' | 'denied' | 'unverified'

interface AuthContextValue {
  user: User | null
  userProfile: UserProfile | null
  /** Signed in, profile load finished, and no fsnap_users doc exists. */
  profileMissing: boolean
  loading: boolean
  profileLoading: boolean
  /** Server-verified Pro status (same as `access === 'granted'`). */
  isPremium: boolean
  access: AccessState
  recheckAccess: () => Promise<void>
  /** No-op on web (profile is a live subscription); kept for mobile-shaped callers. */
  refreshProfile: () => Promise<void>
  signInEmail: (email: string, password: string) => Promise<void>
  signInGoogle: () => Promise<void>
  signInApple: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface ServerVerdict {
  uid: string
  result: WebAccessResult
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  // Set to the uid once the profile subscription delivered a SERVER-confirmed
  // answer for that user — profileMissing must never come from a cached miss.
  const [profileResolvedUid, setProfileResolvedUid] = useState<string | null>(null)
  // The verdict is bound to the uid it was issued for; a uid change resets it.
  const [serverVerdict, setServerVerdict] = useState<ServerVerdict | null>(null)
  const [accessChecking, setAccessChecking] = useState(false)

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u)
      identifyUser(u ? u.uid : null)
      setAuthLoading(false)
      setServerVerdict(null)
      if (!u) {
        setUserProfile(null)
        setProfileResolvedUid(null)
      }
    })
  }, [])

  // Real-time user profile (mirrors mobile AuthContext.subscribeToUserProfile).
  useEffect(() => {
    if (!user) return
    const uid = user.uid
    setProfileLoading(true)
    setProfileResolvedUid(null)
    const unsub = onSnapshot(
      doc(db, 'fsnap_users', uid),
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.exists()) {
          setUserProfile(normalizeProfile(snap.id, snap.data() as Record<string, unknown>))
          setProfileResolvedUid(uid)
        } else if (!snap.metadata.fromCache) {
          setUserProfile(null)
          setProfileResolvedUid(uid)
        }
        setProfileLoading(false)
      },
      (err) => {
        console.error('Profile subscription failed:', err)
        setProfileLoading(false)
      },
    )
    return unsub
  }, [user])

  const verify = useCallback(async (uid: string) => {
    setAccessChecking(true)
    try {
      const result = await fetchWebAccess()
      // Ignore a verdict that arrives after the user changed.
      if (auth.currentUser?.uid === uid) setServerVerdict({ uid, result })
    } catch {
      if (auth.currentUser?.uid === uid) setServerVerdict({ uid, result: 'unverified' })
    } finally {
      setAccessChecking(false)
    }
  }, [])

  // Authoritative access verify (once per auth session).
  useEffect(() => {
    if (!user) return
    void verify(user.uid)
  }, [user, verify])

  const recheckAccess = useCallback(async () => {
    const uid = auth.currentUser?.uid
    if (!uid) return
    await verify(uid)
  }, [verify])

  const profileMissing = !!user && profileResolvedUid === user.uid && userProfile === null
  const hasMirror = userProfile?.isPremium === true
  const verdict = user && serverVerdict?.uid === user.uid ? serverVerdict.result : null

  // Late-webhook auto-recheck: the mirror flips true after the server said
  // denied → ask the server again. The mirror itself never grants anything.
  useEffect(() => {
    if (hasMirror && verdict === 'denied' && !accessChecking) void recheckAccess()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMirror])

  const devBypass = import.meta.env.DEV && import.meta.env.VITE_BYPASS_GATE === '1'

  let access: AccessState
  if (devBypass) access = 'granted'
  else if (verdict === 'granted') access = 'granted'
  else if (verdict === 'denied' || verdict === 'no_profile') access = 'denied'
  else if (verdict === 'unverified' && !accessChecking) access = 'unverified'
  else access = 'checking'

  useEffect(() => {
    if (user) identifyUser(user.uid, access === 'granted')
  }, [user, access])

  const value: AuthContextValue = {
    user,
    userProfile,
    profileMissing,
    loading: authLoading,
    profileLoading,
    isPremium: access === 'granted',
    access,
    recheckAccess,
    refreshProfile: async () => {},
    signInEmail: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password)
    },
    signInGoogle: async () => {
      await signInWithPopup(auth, googleProvider)
    },
    signInApple: async () => {
      await signInWithPopup(auth, appleProvider)
    },
    resetPassword: async (email) => {
      await sendPasswordResetEmail(auth, email)
    },
    signOut: async () => {
      // Shared-computer hygiene. Firestore uses an in-memory cache on the web
      // (see lib/firebase.ts), so no health data survives the page unload;
      // clear the per-user caches we do keep in web storage.
      await clearInsightsCache().catch(() => {})
      await clearGlycemicInsightsCache().catch(() => {})
      clearAllPhotoStash()
      await fbSignOut(auth)
      window.location.replace('/sign-in')
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
